/**
 * API Collection Limits
 *
 * These constants MUST match the backend API limits defined in:
 * MODS.Forge.Common/ModelConfig.cs → Range class (Collection Limits section)
 *
 * Last synced: 2026-01-12
 *
 * ⚠️ IMPORTANT: When backend limits change, update these constants
 * and search the codebase for any hardcoded values that need updating.
 */

export const API_LIMITS = {
  // Collection Limits (from ModelConfig.cs lines 108-115)
  MAX_FACILITY_ASSIGNMENTS: 100,
  MAX_TAG_IDS: 1000,              // Bulk operations limit
  MAX_DB_ID_CHAIN: 100,           // Model element chain depth
  MAX_MODEL_NODES: 5000,          // Max nodes per validation request
  MAX_BULK_TAGS: 500,             // Bulk tag operations
  MAX_EXCEL_ROWS: 10000,          // Excel import limit
  MAX_EXCEL_CELLS: 100,           // Columns per row
  // Rows one bulk register row update may carry (ModelConfig.Range.MaxBulkRegisterRowUpdate).
  // Stricter than the tag bulk limits because the server applies the batch in one Serializable
  // transaction that holds locks on every listed row until commit. Over the cap the API 400s,
  // so the panel has to block Apply before the request rather than after.
  MAX_BULK_REGISTER_ROW_UPDATE: 200,
  // Master-tag CML page size for the batch loader. MUST NOT exceed the backend cap
  // (CmlsController.GetForMasterTag itemsPerPage [Range(1, 500)], which CmlRecordService
  // silently clamps DOWN — it does not 400). Raising this above 500 makes the client
  // request >500, the service return only 500, and the loop break after page 1 → silent
  // truncation. Keep it <= the backend cap.
  MAX_CMLS_PAGE_SIZE: 500,
  // Flat Inspection Monitoring List endpoint page size. The UI walks pages only
  // when one inspection produced more than 500 readings, avoiding CML/TML fan-out.
  MAX_INSPECTION_READINGS_PAGE_SIZE: 500,
  // Page size used while resolving every row in a filtered Register before handing its linked
  // tags to the fullscreen viewer. Kept bounded so each serializable table-data read stays short.
  REGISTER_FILTER_PAGE_SIZE: 500,
};

/**
 * UI Thresholds
 *
 * These are frontend-specific thresholds for UX optimizations.
 * They can differ from API_LIMITS when needed for performance or usability.
 */
export const UI_THRESHOLDS = {
  // Excel processing strategy switches
  EXCEL_INDEXEDDB_THRESHOLD: 5000,    // When to use IndexedDB storage (performance optimization)
  EXCEL_CHUNK_SIZE: 1000,             // Processing chunk size for large datasets
  EXCEL_SAMPLE_SIZE: 1000,            // Preview sample size for data inspection

  // Batch processing
  TAG_BATCH_SIZE: API_LIMITS.MAX_MODEL_NODES,  // Match API limit for node validation

  // 2D viewer tag clustering
  TAG_CLUSTER_THRESHOLD: 1000,  // Show tags individually below this count in 2D view
};

/**
 * Helper Functions
 */
export const LimitValidators = {
  /**
   * Validate if array exceeds tag IDs limit
   */
  validateTagIds(tagIds) {
    return {
      isValid: tagIds.length <= API_LIMITS.MAX_TAG_IDS,
      limit: API_LIMITS.MAX_TAG_IDS,
      count: tagIds.length,
      message: `Cannot process more than ${API_LIMITS.MAX_TAG_IDS} tags at once. Current selection: ${tagIds.length}.`,
    };
  },

  /**
   * Validate if array exceeds bulk tags limit
   */
  validateBulkTags(tags) {
    return {
      isValid: tags.length <= API_LIMITS.MAX_BULK_TAGS,
      limit: API_LIMITS.MAX_BULK_TAGS,
      count: tags.length,
      batchCount: Math.ceil(tags.length / API_LIMITS.MAX_BULK_TAGS),
      message: `Creating ${tags.length} tags in ${Math.ceil(tags.length / API_LIMITS.MAX_BULK_TAGS)} batches (max ${API_LIMITS.MAX_BULK_TAGS} per batch).`,
    };
  },

  /**
   * Validate Excel row count
   */
  validateExcelRows(rowCount) {
    return {
      isValid: rowCount <= API_LIMITS.MAX_EXCEL_ROWS,
      limit: API_LIMITS.MAX_EXCEL_ROWS,
      count: rowCount,
      message: `Excel file contains ${rowCount} rows. Maximum allowed is ${API_LIMITS.MAX_EXCEL_ROWS}.`,
    };
  },

  /**
   * Validate parent chain depth
   */
  validateChainDepth(chain) {
    return {
      isValid: chain.length <= API_LIMITS.MAX_DB_ID_CHAIN,
      limit: API_LIMITS.MAX_DB_ID_CHAIN,
      count: chain.length,
      message: `Parent hierarchy exceeds maximum depth of ${API_LIMITS.MAX_DB_ID_CHAIN} levels. Current depth: ${chain.length}.`,
    };
  },
};

/**
 * Thumbnail Compression Config
 *
 * Controls screenshot compression to stay under the backend's
 * Base64Thumbnail limit (ModelConfig.MaxLength.Base64Thumbnail = 500000 chars).
 *
 * 640x480 JPEG at 0.7 quality ≈ 30-80KB ≈ 40K-107K base64 chars (5-10x headroom).
 */
export const THUMBNAIL_CONFIG = {
  MAX_WIDTH: 640,
  MAX_HEIGHT: 480,
  JPEG_QUALITY: 0.7,
  FORMAT: 'image/jpeg',
};
